import { ReactElement } from 'react';
import styles from './utmb-plus-popin.module.scss';
import { Font } from '@utmb/design-system/text/font';
import { ButtonRemake } from '@utmb/design-system/button-remake/ButtonRemake';
import { Icon } from '@utmb/design-system/icons/Icon';
import { IconEnum } from '@utmb/types/enum/IconEnum';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { DashboardMemberPreferenceCenterView } from '@utmb/types/index';
import { useFormik } from 'formik';
import { getKeycloakToken } from '@utmb/features/sso/keycloak';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
import UserMemberUpdateStatusView from '@utmb/types/view/UserMemberUpdateStatusView';
import { UserMemberUpdateStatusEnum } from '@utmb/types/enum/UserMemberUpdateStatusEnum';
import { toast } from 'react-toastify';
import { useTranslation } from 'next-i18next';

interface UtmbPlusPopinProps {
    closeCallback: (success?: boolean) => void;
}

export function UtmbPlusPopin({ closeCallback }: UtmbPlusPopinProps): ReactElement {
    const { t } = useTranslation();
    const { dashboardMember, updateDashboardMemberPreferenceCenter } = useDashboard();
    const { optInWs, optInLive, optInIndex } = dashboardMember?.preferenceCenter || ({} as DashboardMemberPreferenceCenterView);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            optInWs: Boolean(optInWs),
            optInLive: Boolean(optInLive),
            optInIndex: Boolean(optInIndex),
        },
        onSubmit: async (values) => {
            const token = await getKeycloakToken();

            const response = (await fetchTheAPI({
                isClient: true,
                path: '/users/dashboard/member/preference-center',
                bearer: token,
                method: FetchMethodsEnum.PUT,
                body: values,
            })) as UserMemberUpdateStatusView;

            if (response.statusCode === UserMemberUpdateStatusEnum.OK) {
                updateDashboardMemberPreferenceCenter(values as DashboardMemberPreferenceCenterView);
                closeCallback(true);
            } else {
                toast.error(t('myutmb:myUtmb.myInformation.errorMessage'));
            }
        },
    });

    function handleAccept(): void {
        formik.values.optInWs = true;
        formik.handleSubmit();
        closeCallback();
    }

    return (
        <div className={styles.utmbPlusPopin}>
            <div className={styles.container}>
                <button className={styles.cross} onClick={() => closeCallback()}>
                    <Icon variant={IconEnum.CLOSE} color={getColorStyle(ColorTypesEnum.PRIMARY)} width={20} height={20} />
                </button>
                <Font.FuturaBold className={styles.title} desktop="20" mobile="20">
                    {t('myutmb:myUtmb.loyalty.registerPlaceholder.title')}
                </Font.FuturaBold>
                <Font.FuturaMedium className={styles.description} desktop="16" mobile="16">
                    <span dangerouslySetInnerHTML={{ __html: t('myutmb:myUtmb.loyalty.registerPlaceholder.description') }} />
                </Font.FuturaMedium>
                <div className={styles.wrapButton}>
                    <ButtonRemake
                        customClassName={styles.cancelButton}
                        label={t('myutmb:myUtmb.loyalty.registerPlaceholder.decline')}
                        action={() => closeCallback()}
                        variant="solid"
                    />
                    <ButtonRemake label={t('myutmb:myUtmb.loyalty.registerPlaceholder.accept')} action={() => handleAccept()} variant="solid" />
                </div>
            </div>
        </div>
    );
}
